import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  AlertCircle,
  Loader2,
  Minus,
  Plus,
  Printer,
  Search,
  ShoppingBag,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react';
import { PosSettlementType, RetailLookupType } from '@pms/shared';
import type { RetailHouseAccount, RetailProduct, SaleReceipt } from '@pms/shared';
import { UserRole } from '@pms/shared';
import { usePropertyContext } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import { useRetailLookups } from '@/hooks/api/useRetailLookups';
import { useRetailHouseAccounts } from '@/hooks/api/useRetailHouseAccounts';
import { useBookings } from '@/hooks/api/useBookings';
import type { Booking } from '@/services/api/bookings.api';
import { useConfirmSale, usePosProducts, usePosSettings } from '@/hooks/api/usePosSales';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatMoney } from '@/lib/money';
import { extractErrorMessage } from '@/lib/error';
import { cn } from '@/lib/utils';
import {
  cartReducer,
  computeTotals,
  emptyCart,
  type CartLine,
} from './posCart';

// Roles permitted to operate the terminal.
const TERMINAL_ROLES = new Set<UserRole>([
  UserRole.OPERATIONS,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

// ─── ProductTile ────────────────────────────────────────────────────────────

function ProductTile({
  product,
  onAdd,
}: {
  product: RetailProduct;
  onAdd: (p: RetailProduct) => void;
}) {
  const outOfStock = product.stockOnHand <= 0;
  return (
    <button
      type="button"
      onClick={() => onAdd(product)}
      disabled={outOfStock}
      aria-disabled={outOfStock}
      className={cn(
        'flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        outOfStock
          ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
          : 'border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50',
      )}
    >
      <span className="line-clamp-2 text-sm font-medium text-gray-900">{product.name}</span>
      <span className="text-xs text-gray-400">{product.sku}</span>
      <div className="mt-1 flex w-full items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">{formatMoney(product.price)}</span>
        {outOfStock ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-600">
            Out of stock
          </span>
        ) : (
          <span className="text-[11px] text-gray-400">{product.stockOnHand} in stock</span>
        )}
      </div>
    </button>
  );
}

// ─── ProductGrid ──────────────────────────────────────────────────────────────

function ProductGrid({
  propertyId,
  onAdd,
  inlineWarning,
}: {
  propertyId: string;
  onAdd: (p: RetailProduct) => void;
  inlineWarning: string | null;
}) {
  const [search, setSearch] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus the search on mount so a UPC can be typed/scanned immediately.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const { data: departmentsData } = useRetailLookups({
    type: RetailLookupType.DEPARTMENT,
    propertyId,
    isActive: true,
  });
  const departments = departmentsData?.data ?? [];

  const { data: products, isLoading, isError, error } = usePosProducts({
    propertyId,
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(departmentId ? { departmentId } : {}),
  });

  const list = products ?? [];

  // UPC-enter: exact UPC match in the current set → add directly.
  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    const q = search.trim();
    if (!q) return;
    const match = list.find((p) => p.upc && p.upc === q);
    if (match && match.stockOnHand > 0) {
      onAdd(match);
      setSearch('');
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b border-gray-100 p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by name, SKU or UPC…"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {departments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setDepartmentId('')}
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                departmentId === '' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              All
            </button>
            {departments.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDepartmentId(d.id)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  departmentId === d.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                )}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}
        {inlineWarning && (
          <div role="alert" className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle size={13} aria-hidden="true" />
            {inlineWarning}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        )}
        {isError && (
          <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle size={14} aria-hidden="true" />
            {extractErrorMessage(error)}
          </div>
        )}
        {!isLoading && !isError && list.length === 0 && (
          <EmptyState
            icon={ShoppingBag}
            title="No products available"
            description="Add some in Products, or adjust your search."
          />
        )}
        {!isLoading && !isError && list.length > 0 && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {list.map((p) => (
              <ProductTile key={p.id} product={p} onAdd={onAdd} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CartLine row ─────────────────────────────────────────────────────────────

function CartLineRow({
  line,
  highlight,
  onInc,
  onDec,
  onSetQty,
  onRemove,
}: {
  line: CartLine;
  highlight: boolean;
  onInc: () => void;
  onDec: () => void;
  onSetQty: (qty: number) => void;
  onRemove: () => void;
}) {
  const overStock = line.qty > line.stockOnHand;
  return (
    <div
      className={cn(
        'border-b border-gray-50 px-4 py-3',
        highlight ? 'bg-red-50' : '',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">{line.name}</p>
          <p className="text-xs text-gray-400">
            {formatMoney(line.unitPrice)} · {line.sku}
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${line.name}`}
          className="rounded p-1 text-gray-300 transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onDec}
            aria-label="Decrease quantity"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            <Minus size={13} aria-hidden="true" />
          </button>
          <input
            type="number"
            min={1}
            step={1}
            value={line.qty}
            onChange={(e) => onSetQty(Number(e.target.value))}
            aria-label={`Quantity for ${line.name}`}
            className="h-7 w-12 rounded-md border border-gray-200 text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            type="button"
            onClick={onInc}
            aria-label="Increase quantity"
            className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            <Plus size={13} aria-hidden="true" />
          </button>
        </div>
        <span className="text-sm font-semibold text-gray-900">
          {formatMoney(line.unitPrice * line.qty)}
        </span>
      </div>
      {overStock && (
        <p className="mt-1 text-xs text-amber-600">Only {line.stockOnHand} in stock</p>
      )}
    </div>
  );
}

// ─── TotalsStrip ──────────────────────────────────────────────────────────────

function TotalsStrip({
  subtotal,
  tax,
  total,
  taxRatePercent,
  disabled,
  onTender,
}: {
  subtotal: number;
  tax: number;
  total: number;
  taxRatePercent: number;
  disabled: boolean;
  onTender: () => void;
}) {
  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <div className="space-y-1.5 text-sm" aria-live="polite">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span>{formatMoney(subtotal)}</span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>Tax{taxRatePercent > 0 ? ` (${taxRatePercent}%)` : ''}</span>
          <span>{formatMoney(tax)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-100 pt-1.5 text-base font-bold text-gray-900">
          <span>Total</span>
          <span>{formatMoney(total)}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onTender}
        disabled={disabled}
        className="mt-3 w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Tender {formatMoney(total)}
      </button>
    </div>
  );
}

// ─── Centered modal overlay (focus-trapped) ───────────────────────────────────

function ModalOverlay({
  onClose,
  labelledBy,
  children,
}: {
  onClose: () => void;
  labelledBy: string;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const restore = document.activeElement as HTMLElement | null;
    // Move focus into the dialog on open.
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (nodes.length === 0) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restore?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
      >
        {children}
      </div>
    </div>
  );
}

// ─── ChargeToRoomTab ──────────────────────────────────────────────────────────

function ChargeToRoomTab({
  propertyId,
  selectedId,
  onSelect,
  onSwitchToCash,
}: {
  propertyId: string;
  selectedId: string | null;
  onSelect: (b: Booking) => void;
  onSwitchToCash: () => void;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useBookings({ propertyId, status: 'checked_in', limit: 200 });
  const raw: Booking[] = Array.isArray(data)
    ? (data as Booking[])
    : ((data as { data?: Booking[] })?.data ?? []);
  const inHouse = raw.filter((b) => b.status === 'checked_in');

  const filtered = inHouse.filter((b) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      b.guest?.fullName?.toLowerCase().includes(q) ||
      b.unit?.name?.toLowerCase().includes(q) ||
      b.id.toLowerCase().includes(q)
    );
  });

  if (!isLoading && inHouse.length === 0) {
    return (
      <div className="px-4 py-8">
        <EmptyState
          icon={ShoppingCart}
          title="No in-house guests to charge"
          description="Use Cash/Card to tender this sale."
          actionLabel="Switch to Cash/Card"
          onAction={onSwitchToCash}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="border-b border-gray-100 p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guest, room…"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gray-500">No bookings match your search.</p>
        )}
        {!isLoading &&
          filtered.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => onSelect(b)}
              className={cn(
                'w-full border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
                selectedId === b.id ? 'border-l-2 border-l-primary-500 bg-primary-50' : '',
              )}
            >
              <p className="truncate text-sm font-medium text-gray-900">{b.guest?.fullName ?? 'Guest'}</p>
              <p className="mt-0.5 text-xs text-gray-500">{b.unit?.name ?? '—'}</p>
            </button>
          ))}
      </div>
    </div>
  );
}

// ─── HouseAccountTab ──────────────────────────────────────────────────────────

function HouseAccountTab({
  propertyId,
  selectedId,
  onSelect,
  onSwitchToCash,
}: {
  propertyId: string;
  selectedId: string | null;
  onSelect: (a: RetailHouseAccount) => void;
  onSwitchToCash: () => void;
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useRetailHouseAccounts({ propertyId, activeOnly: true });
  const accounts: RetailHouseAccount[] = (data ?? []).filter((a) => a.isActive);

  const filtered = accounts.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.name.toLowerCase().includes(q) ||
      (a.code ?? '').toLowerCase().includes(q)
    );
  });

  if (!isLoading && accounts.length === 0) {
    return (
      <div className="px-4 py-8">
        <EmptyState
          icon={ShoppingCart}
          title="No house accounts"
          description="Use Cash/Card to tender this sale."
          actionLabel="Switch to Cash/Card"
          onAction={onSwitchToCash}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="border-b border-gray-100 p-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search account, code…"
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-gray-400" />
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-gray-500">No accounts match your search.</p>
        )}
        {!isLoading &&
          filtered.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a)}
              className={cn(
                'w-full border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
                selectedId === a.id ? 'border-l-2 border-l-primary-500 bg-primary-50' : '',
              )}
            >
              <p className="truncate text-sm font-medium text-gray-900">
                {a.name}
                {a.code ? <span className="ml-1 font-normal text-gray-400">· {a.code}</span> : null}
              </p>
              <p className="mt-0.5 text-xs text-gray-500">Balance {formatMoney(a.balance)}</p>
            </button>
          ))}
      </div>
    </div>
  );
}

// ─── CashCardTab ──────────────────────────────────────────────────────────────

function CashCardTab({
  total,
  method,
  onMethod,
  amount,
  onAmount,
}: {
  total: number;
  method: 'cash' | 'card';
  onMethod: (m: 'cash' | 'card') => void;
  amount: string;
  onAmount: (v: string) => void;
}) {
  const tendered = parseFloat(amount);
  const valid = Number.isFinite(tendered) && tendered >= total;
  const change = valid ? tendered - total : 0;

  return (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
        {(['cash', 'card'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onMethod(m)}
            className={cn(
              'rounded-md py-2 text-sm font-medium capitalize transition-colors',
              method === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {m}
          </button>
        ))}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Amount tendered</label>
        <input
          type="number"
          min="0"
          step="0.01"
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          onBlur={(e) => {
            const n = parseFloat(e.target.value);
            if (Number.isFinite(n)) onAmount(n.toFixed(2));
          }}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5 text-sm" aria-live="polite">
        <span className="text-gray-600">Change due</span>
        <span className="font-semibold text-gray-900">{formatMoney(change)}</span>
      </div>

      {!valid && (
        <p role="alert" className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle size={12} aria-hidden="true" />
          Amount is less than the total ({formatMoney(total)}).
        </p>
      )}

      {method === 'card' && (
        <p className="text-xs text-gray-400">Recorded as paid at property — no card is charged here.</p>
      )}
    </div>
  );
}

// ─── Receipt ──────────────────────────────────────────────────────────────────

function ReceiptView({
  receipt,
  onNewSale,
  onClose,
}: {
  receipt: SaleReceipt;
  onNewSale: () => void;
  onClose: () => void;
}) {
  const isRoom = receipt.settlement.type === PosSettlementType.CHARGE_TO_ROOM;
  const isHouse = receipt.settlement.type === PosSettlementType.HOUSE_ACCOUNT;
  return (
    <>
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 id="pos-tender-title" className="text-base font-semibold text-gray-900">
          Sale {receipt.saleNumber}
        </h2>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4" id="pos-receipt-print">
        <p className="text-xs text-gray-400">
          {new Date(receipt.createdAt).toLocaleString()}
          {receipt.operator ? ` · ${receipt.operator}` : ''}
        </p>

        <table className="mt-3 w-full text-sm">
          <tbody>
            {receipt.items.map((it, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="py-1.5">
                  <span className="text-gray-900">{it.name}</span>
                  <span className="text-gray-400"> × {it.qty}</span>
                </td>
                <td className="py-1.5 text-right font-medium text-gray-900">{formatMoney(it.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal</span>
            <span>{formatMoney(receipt.subtotal)}</span>
          </div>
          <div className="flex justify-between text-gray-600">
            <span>Tax</span>
            <span>{formatMoney(receipt.taxTotal)}</span>
          </div>
          <div className="flex justify-between text-base font-bold text-gray-900">
            <span>Total</span>
            <span>{formatMoney(receipt.total)}</span>
          </div>
        </div>

        <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2.5 text-sm">
          {isRoom ? (
            <p className="text-gray-700">
              Charged to room {receipt.settlement.room ?? '—'}
              {receipt.settlement.guestName ? ` — ${receipt.settlement.guestName}` : ''} (folio).
            </p>
          ) : isHouse ? (
            <p className="text-gray-700">
              Charged to house account {receipt.settlement.houseAccountName ?? '—'}
              {receipt.settlement.newBalance != null
                ? ` — new balance ${formatMoney(receipt.settlement.newBalance)}`
                : ''}
              .
            </p>
          ) : (
            <div className="space-y-1">
              <div className="flex justify-between text-gray-600">
                <span className="capitalize">{receipt.settlement.method ?? 'Cash'} tendered</span>
                <span>{formatMoney(receipt.settlement.tenderAmount ?? '0')}</span>
              </div>
              <div className="flex justify-between font-medium text-gray-900">
                <span>Change due</span>
                <span>{formatMoney(receipt.settlement.changeDue ?? '0')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-t border-gray-200 px-5 py-4">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Printer size={15} aria-hidden="true" /> Print
        </button>
        <button
          type="button"
          onClick={onNewSale}
          className="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
        >
          New sale
        </button>
      </div>
    </>
  );
}

// ─── TenderModal ──────────────────────────────────────────────────────────────

function TenderModal({
  propertyId,
  total,
  items,
  idempotencyKey,
  onClose,
  onSuccess,
  onOutOfStock,
}: {
  propertyId: string;
  total: number;
  items: { productId: string; qty: number }[];
  idempotencyKey: string;
  onClose: () => void;
  onSuccess: (r: SaleReceipt) => void;
  onOutOfStock: (productId: string, available: number, message: string) => void;
}) {
  const [tab, setTab] = useState<'room' | 'cash' | 'house'>('room');
  const [booking, setBooking] = useState<Booking | null>(null);
  const [account, setAccount] = useState<RetailHouseAccount | null>(null);
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  const [amount, setAmount] = useState(total.toFixed(2));
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const confirm = useConfirmSale();

  const tendered = parseFloat(amount);
  const cashValid = Number.isFinite(tendered) && tendered >= total;
  const canConfirm =
    !confirm.isPending &&
    (tab === 'room' ? !!booking : tab === 'house' ? !!account : cashValid);

  function handleConfirm() {
    setErrorMsg(null);
    const settlement =
      tab === 'room'
        ? { type: PosSettlementType.CHARGE_TO_ROOM, bookingId: booking!.id }
        : tab === 'house'
          ? { type: PosSettlementType.HOUSE_ACCOUNT, accountId: account!.id }
          : { type: method === 'cash' ? PosSettlementType.CASH : PosSettlementType.CARD, tenderAmount: Number(tendered.toFixed(2)), method };

    confirm.mutate(
      { payload: { propertyId, items, settlement }, idempotencyKey },
      {
        onSuccess: (r) => onSuccess(r),
        onError: (err) => {
          const resp = (err as { response?: { status?: number; data?: { code?: string; productId?: string; available?: number; message?: string } } }).response;
          const data = resp?.data;
          if (resp?.status === 409 && data?.code === 'OUT_OF_STOCK' && data.productId) {
            onOutOfStock(data.productId, data.available ?? 0, data.message ?? 'Sold out while ringing up.');
            onClose();
            return;
          }
          setErrorMsg(extractErrorMessage(err));
        },
      },
    );
  }

  return (
    <ModalOverlay onClose={onClose} labelledBy="pos-tender-title">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <h2 id="pos-tender-title" className="text-base font-semibold text-gray-900">
          Tender · {formatMoney(total)}
        </h2>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <X size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="flex border-b border-gray-200 px-5">
        <button
          type="button"
          onClick={() => setTab('room')}
          className={cn(
            'border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            tab === 'room' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          Charge to room
        </button>
        <button
          type="button"
          onClick={() => setTab('cash')}
          className={cn(
            'border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            tab === 'cash' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          Cash / Card
        </button>
        <button
          type="button"
          onClick={() => setTab('house')}
          className={cn(
            'border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            tab === 'house' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700',
          )}
        >
          House Account
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === 'room' ? (
          <ChargeToRoomTab
            propertyId={propertyId}
            selectedId={booking?.id ?? null}
            onSelect={setBooking}
            onSwitchToCash={() => setTab('cash')}
          />
        ) : tab === 'house' ? (
          <HouseAccountTab
            propertyId={propertyId}
            selectedId={account?.id ?? null}
            onSelect={setAccount}
            onSwitchToCash={() => setTab('cash')}
          />
        ) : (
          <CashCardTab
            total={total}
            method={method}
            onMethod={setMethod}
            amount={amount}
            onAmount={setAmount}
          />
        )}
      </div>

      {errorMsg && (
        <div role="alert" className="mx-5 mb-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={14} aria-hidden="true" />
          {errorMsg}
        </div>
      )}

      <div className="border-t border-gray-200 px-5 py-4">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {confirm.isPending
            ? 'Processing…'
            : tab === 'room'
              ? 'Confirm — charge to room'
              : tab === 'house'
                ? 'Confirm — house account'
                : `Confirm — ${method === 'cash' ? 'Cash' : 'Card'}`}
        </button>
      </div>
    </ModalOverlay>
  );
}

// ─── PosTerminalPage ──────────────────────────────────────────────────────────

export function PosTerminalPage() {
  const { selectedProperty } = usePropertyContext();
  const { user } = useAuth();
  const propertyId = selectedProperty?.id ?? '';

  const [cart, dispatch] = useReducer(cartReducer, emptyCart);
  const [gridWarning, setGridWarning] = useState<string | null>(null);
  const [highlightProductId, setHighlightProductId] = useState<string | null>(null);

  // One Idempotency-Key per tender attempt. Minted when the tender modal opens
  // and on [New sale]; regenerated whenever the cart is edited (a genuinely
  // different request) so a post-409 retry never replays a stale attempt.
  const [tenderKey, setTenderKey] = useState<string>(() => crypto.randomUUID());
  const [tenderOpen, setTenderOpen] = useState(false);
  const [receipt, setReceipt] = useState<SaleReceipt | null>(null);

  const { data: settings } = usePosSettings(propertyId);
  const taxRatePercent = settings ? parseFloat(settings.taxRatePercent) || 0 : 0;

  const totals = useMemo(
    () => computeTotals(cart.lines, taxRatePercent),
    [cart.lines, taxRatePercent],
  );

  // Warn before leaving the page (reload/close) with a non-empty cart.
  useEffect(() => {
    if (cart.lines.length === 0) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [cart.lines.length]);

  // Role guard — defence-in-depth (nav already filters).
  if (user && !TERMINAL_ROLES.has(user.role)) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <EmptyState icon={ShoppingBag} title="Not available" description="The sell terminal isn't available for your role." />
      </div>
    );
  }

  if (!selectedProperty) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <EmptyState icon={ShoppingBag} title="Select a property" description="Choose a property from the sidebar to open the terminal." />
      </div>
    );
  }

  // Any cart edit invalidates the current tender attempt's key.
  function regenerateKey() {
    setTenderKey(crypto.randomUUID());
  }

  function handleAdd(p: RetailProduct) {
    if (p.stockOnHand <= 0) {
      setGridWarning(`${p.name} is out of stock.`);
      return;
    }
    setGridWarning(null);
    setHighlightProductId(null);
    dispatch({ type: 'ADD', product: p });
    regenerateKey();
  }

  function handleClear() {
    if (cart.lines.length === 0) return;
    if (window.confirm('Clear the whole cart?')) {
      dispatch({ type: 'CLEAR' });
      regenerateKey();
    }
  }

  return (
    <div className="flex h-[calc(100vh-48px)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Left — product grid */}
      <div className="min-w-0 flex-1 border-r border-gray-200">
        <ProductGrid propertyId={propertyId} onAdd={handleAdd} inlineWarning={gridWarning} />
      </div>

      {/* Right — cart */}
      <div className="flex w-80 shrink-0 flex-col bg-white xl:w-96">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-bold text-gray-900">
            <ShoppingCart size={16} aria-hidden="true" /> Cart
          </h2>
          {cart.lines.length > 0 && (
            <button type="button" onClick={handleClear} className="text-xs font-medium text-gray-400 hover:text-red-500">
              Clear cart
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.lines.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center px-6 text-center">
              <ShoppingCart size={32} className="mb-2 text-gray-200" aria-hidden="true" />
              <p className="text-sm text-gray-400">Cart is empty — tap a product to start.</p>
            </div>
          ) : (
            cart.lines.map((line) => (
              <CartLineRow
                key={line.productId}
                line={line}
                highlight={highlightProductId === line.productId}
                onInc={() => {
                  dispatch({ type: 'INC', productId: line.productId });
                  regenerateKey();
                }}
                onDec={() => {
                  dispatch({ type: 'DEC', productId: line.productId });
                  regenerateKey();
                }}
                onSetQty={(qty) => {
                  dispatch({ type: 'SET_QTY', productId: line.productId, qty });
                  regenerateKey();
                }}
                onRemove={() => {
                  dispatch({ type: 'REMOVE', productId: line.productId });
                  regenerateKey();
                }}
              />
            ))
          )}
        </div>

        <TotalsStrip
          subtotal={totals.subtotal}
          tax={totals.tax}
          total={totals.total}
          taxRatePercent={taxRatePercent}
          disabled={cart.lines.length === 0}
          onTender={() => {
            // Fresh attempt — mint a key for this tender.
            regenerateKey();
            setTenderOpen(true);
          }}
        />
      </div>

      {tenderOpen && !receipt && (
        <TenderModal
          propertyId={propertyId}
          total={totals.total}
          items={cart.lines.map((l) => ({ productId: l.productId, qty: l.qty }))}
          idempotencyKey={tenderKey}
          onClose={() => setTenderOpen(false)}
          onSuccess={(r) => setReceipt(r)}
          onOutOfStock={(productId, _available, message) => {
            setHighlightProductId(productId);
            setGridWarning(message);
            // Cart preserved; mint a fresh key so the corrected retry is a new attempt.
            regenerateKey();
          }}
        />
      )}

      {receipt && (
        <ModalOverlay onClose={() => setReceipt(null)} labelledBy="pos-tender-title">
          <ReceiptView
            receipt={receipt}
            onNewSale={() => {
              dispatch({ type: 'CLEAR' });
              regenerateKey();
              setReceipt(null);
              setTenderOpen(false);
            }}
            onClose={() => {
              setReceipt(null);
              setTenderOpen(false);
            }}
          />
        </ModalOverlay>
      )}
    </div>
  );
}
